import {
  belongsOnRoad,
  isCounterCollection,
  isPreorderDueNow,
  sumCents,
  tt,
  type FilterChannel,
} from '@agos/shared'

// §9.343 - the settling-status rule moved to `@agos/shared` when a third caller needed it. Re-exported
// so everything that already imported it from here is unaffected by where it lives.
export {
  SETTLING_STATUSES,
  credentialsNeeded,
  protectedStatusPlan,
  statusRoleNote,
} from '@agos/shared'

import {
  elapsedLabel,
  isDispatchable,
  isOnTheBoard,
} from './dispatch-rules'

/**
 * Which orders the dispatch board shows, in what order, and what it says about
 * the drivers currently out.
 *
 * Ported from legacy `pages/dispatch.tsx`.
 */

export interface BoardOrder {
  id?: number
  orderNumber?: string
  type?: string
  status?: string
  createdAt?: string
  /**
   * The fiscal document's state - §9.442, FB-761.
   *
   * **Type-only**: `DispatchPage` builds these with `(result?.data ?? []) as BoardOrder[]`, a cast
   * over the rows `GET /orders` answers, and `OrderDtos`'s own comment says the **list** read
   * *"answers the whole row - every fiscal, delivery-run and SMS column - because the dispatch board
   * and the archive need them"*. So the values were always there and this interface was hiding them.
   *
   * Two fields rather than the seven the row carries, because that is what `mayStornoFiskal` reads:
   * a document exists and nobody has reversed it.
   */
  fiskalPrintedAt?: string
  fiskalStornoAt?: string
  /**
   * When the van left - §9.438.
   *
   * `OrderDispatchService`: *"onTheWayAt is the departure, and the trip log measures the journey
   * from it."* It has been on `OrderListItem` all along and no screen read it.
   */
  onTheWayAt?: string | null
  scheduledAt?: string
  driverId?: number | null
  driverName?: string
  /** The waiter who took it. Null on a bar bill nobody attributed — FB-075's gap. */
  employeeId?: number | null
  total?: number | string
  /**
   * What the customer wrote — §9.54.
   *
   * `/orders` has always carried it and this type never named it, so the board could not draw it.
   * Nobody typing a note knows who it is for: *zvoňte na susedov* is for the driver this screen
   * assigns.
   */
  notes?: string | null
  /**
   * Who the bag is for, and how much to trust the order — §9.125, the owner's point 11.
   *
   * `orderFull` has always carried every one of these and this type named none of them, so the board
   * could not draw what it was already being sent. The same omission as `notes` above, found the same
   * way: the owner asked where the information went.
   */
  customerName?: string
  customerPhone?: string
  customerEmail?: string
  customerAddress?: string | null
  /** `web` for an order nobody spoke to, `phone` for one somebody took. */
  source?: string
  paymentStatus?: string
  /**
   * How it will be paid, and what was taken off — §9.154.
   *
   * <p>Both have been on the `/orders` payload since the beginning and this type named neither, so
   * the board drew a total with no idea whether the money had arrived, was coming with the courier,
   * or had been discounted on the way. The same omission as `notes` and the customer block above.
   */
  paymentMethod?: string
  discount?: number | string
  /** A pre-order has a promised time; §9.154 draws the countdown to it. */
  isPreorder?: boolean
  isEdited?: boolean
  /** How many orders this number has placed. Used from **two** upward — see `verificationOf`. */
  phoneOrderCount?: number
  emailOrderCount?: number
  /**
   * Where it is going, and which zone the server put it in - §9.351, §10A.139.
   *
   * `deliveryLat` has been on the `/orders` payload since the beginning and this type never named it;
   * `deliveryZoneName` is new, because until §10A.139 the server resolved the zone and threw the
   * answer away. **Both are needed together** and neither means anything alone - see
   * {@link outsideEveryZone}.
   */
  deliveryLat?: number | null
  deliveryZoneName?: string | null
  /**
   * Whether the server looked for a zone at all - `V55`, correcting §10A.139.
   *
   * **This is what makes a null name readable.** `V54` back-fills nothing, so every order written
   * before it has coordinates and no zone name - and the first version of {@link outsideEveryZone}
   * would have badged all 6 070 of the owner's geocoded orders. Measured, only **3** of them are in
   * a status the board shows, so the effect was bounded to orders in flight at deployment - but a
   * badge that says *mimo zóny* about an order inside a zone is a screen stating a falsehood, and
   * presenting it as normal is the failure this repository keeps finding.
   */
  deliveryZoneChecked?: boolean
  /**
   * External couriers - §9.786, BACKEND.md §10A.438 E. What the courier earns for this delivery, when the order was
   * offered to them, and when one claimed it. Absent on an order nobody offered; read by `courier-board.ts`.
   */
  driverReward?: number | string | null
  offeredAt?: string | null
  claimedAt?: string | null
}

/**
 * Whether the server looked for a delivery zone and found none - §9.351.
 *
 * **The flag is a pair of columns, not a field**, and that is deliberate (§10A.139): a null
 * `deliveryZoneName` means two different things depending on whether the order has coordinates.
 *
 * | checked | coordinates | zone name | what it is |
 * |---|---|---|---|
 * | yes | present | a name | inside that zone |
 * | **yes** | **present** | **null** | **the server looked and found none** - 147 of the owner's orders |
 * | yes | null | null | no coordinates, so no rule could apply - 1 248 of them |
 * | **no** | anything | anything | **written before the recording existed** - every historical order |
 *
 * **Badging the third case would bury the second.** A phone order that somebody typed an address
 * into has no coordinates and therefore no zone by construction; there are 1 248 of those against
 * 147 that mean something. So the rule is *coordinates and no zone*, written once here rather than
 * spelled at each call site - which is how §10A.136's one-reader-spelled-twice happened.
 *
 * Nothing about this refuses or blocks: §10A.139 accepts every one of these, and the owner's
 * *"we can loose a lot of money"* is why. It is a marker for whoever is assigning a driver.
 */
export function outsideEveryZone(order: BoardOrder): boolean {
  if (order.deliveryZoneChecked !== true) return false
  const hasCoordinates = order.deliveryLat !== null && order.deliveryLat !== undefined
  const named = (order.deliveryZoneName ?? '').trim()
  return hasCoordinates && named === ''
}

/**
 * Whether a pre-order's day has arrived.
 *
 * Scheduled *today* or already overdue is today's work; anything further out is not. Compared by
 * local day, not by instant — a delivery booked for 19:00 tonight is today's whatever the clock says
 * now.
 *
 * **This used to decide whether the row was on the board at all, and §9.318 makes it decide which
 * band it goes in** (owner Q8: *"Predobjednávky - not showing in kuchyna, rozvoz - nástenka etc."*).
 * The old comment said a later-day pre-order *"lives on the pre-order screen"* while the kitchen's
 * said it *"belongs on the dispatch board until it comes due"* - each screen handed it to the other
 * and it appeared on neither.
 */
export function hasComeDue(
  order: BoardOrder,
  now: Date,
  leadMinutes: number,
): boolean {
  return isPreorderDueNow(order.scheduledAt, leadMinutes, now.getTime())
}

/**
 * A pre-order whose day has not come — §9.318.
 *
 * `isPreorder` is **not** required: the flag and `scheduledAt` have disagreed on real rows (§10A.88's
 * neighbourhood), and a scheduled time in a later day is the thing that makes it not-yet-due whatever
 * the flag says. A row with no `scheduledAt` can never be one.
 */
export function isNotYetDuePreorder(
  order: BoardOrder,
  now: Date,
  leadMinutes: number,
): boolean {
  return Boolean(order.scheduledAt) && !hasComeDue(order, now, leadMinutes)
}

/**
 * The board is the road, so table orders are not on it — the restaurant screen
 * owns those.
 *
 * **§9.5.** This was `order.type !== 'restaurant'`, which stopped being the same
 * statement the moment §10A.1 let a franchise add a second table-serving
 * channel. `na_sekeru` is exactly that channel, and it would have appeared on
 * the road. `belongsOnRoad` asks the capability — `usesTables` — instead.
 *
 * The channels are a **required** argument rather than an optional one with a
 * fallback: a fallback would silently reproduce the bug for any caller that
 * forgot, and forgetting is the whole failure mode. The page waits for them
 * before drawing the board, so a table order never flashes onto the road.
 */
export function boardOrders(
  orders: BoardOrder[],
  channels: readonly FilterChannel[],
  now: Date,
  leadMinutes: number,
): BoardOrder[] {
  /* §9.643, owner O34 - **the clock is back in membership, and §9.318's band is gone with it.**
   *
   * The owner: *"Predobjednávka - nezobrazovať na rozvoz - nástenka - dokým nebude hodinu pred"*. That is
   * literal, so a preorder that has not come due is not on the board at all rather than in a band of its own.
   *
   * **This narrows §9.318 rather than undoing it**, and the difference is a screen that did not exist when
   * owner Q8 was raised. Q8 was *"Predobjednávky - not showing in kuchyna, rozvoz - nástenka etc."* - the
   * defect being that each screen handed it to the other and it appeared on **neither**. `PreordersPage` now
   * lists every preorder, open and closed, sorted by `scheduledAt` with its own countdown, so nothing is
   * invisible: the board is the road and shows what is about to go on it.
   *
   * `isPreorderDueNow` is the kitchen's own rule and `leadMinutes` the franchise's own setting, so the two
   * screens can no longer disagree about when an order starts existing. */
  return orders.filter(
    (order) => belongsOnRoad(order.type ?? '', channels) && hasComeDue(order, now, leadMinutes),
  )
}

/** The status an order waits in until somebody on the board presses *✓ Prijať* - `NEXT_STATUSES.pending`. */
export const AWAITING_ACCEPTANCE = 'pending'

/**
 * The orders the board's first band draws, *Čakajú na prijatie* - §9.674, owner O58.
 *
 * **The board's own membership and then the status, so the shell's red count is this band.** The server's
 * `total` for `?status=pending` cannot be that number: its `date_from` admits any order scheduled on or after the
 * day, so it would count tomorrow's preorder that `boardOrders` keeps off the road until the lead time (§9.643),
 * and a pending order on a table channel that belongs to the restaurant screen. Counting through `boardOrders`
 * rather than restating its two conditions is what stops the badge and the board disagreeing the next time
 * either rule moves.
 */
export function awaitingAcceptance(
  orders: BoardOrder[],
  channels: readonly FilterChannel[],
  now: Date,
  leadMinutes: number,
): BoardOrder[] {
  return boardOrders(orders, channels, now, leadMinutes).filter(
    (order) => order.status === AWAITING_ACCEPTANCE,
  )
}

export function activeOrders(orders: BoardOrder[]): BoardOrder[] {
  return orders.filter((order) => isOnTheBoard(order.status))
}

/**
 * Counter collections first, then oldest.
 *
 * Somebody is standing at the counter waiting for a collection; a delivery has a
 * driver's journey still to absorb the wait. Legacy sorts this way and the reason
 * is the same one the kitchen puts seated guests before deliveries.
 */
export function sortForBoard(
  orders: BoardOrder[],
  channels: readonly FilterChannel[],
): BoardOrder[] {
  return [...orders].sort((left, right) => {
    // §9.5: on the premises but not at a table, rather than the literal `bar`.
    const byCollection = (order: BoardOrder) =>
      isCounterCollection(order.type ?? '', channels) ? 0 : 1
    if (byCollection(left) !== byCollection(right)) {
      return byCollection(left) - byCollection(right)
    }
    return new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime()
  })
}

/**
 * The board's tabs: `all`, `active`, or one exact status.
 *
 * `all` is the owner's request — *"nejde si zobraziť všetko"*. It is genuinely everything the board
 * holds rather than a wider status list: `boardOrders` has already scoped to today's road orders, so
 * *all* means every status within that, delivered and cancelled included. Without it there was no way
 * to see an order once it left `active`, and the seven status tabs are only useful if you already know
 * which one it landed in.
 *
 * `active` stays the **default**. It is the working view — what still needs doing — and opening onto a
 * list padded with this morning's delivered orders would cost the dispatcher the thing they came for.
 */
export function visibleOrders(
  orders: BoardOrder[],
  filterStatus: string,
  channels: readonly FilterChannel[],
  now: Date,
  leadMinutes: number,
): BoardOrder[] {
  /* §9.643, owner O34 - the clock is `boardOrders`' argument again, and it has to reach here too: a filtered
     view that showed what the unfiltered board hides would be the same order appearing and disappearing as the
     dispatcher changes tab. */
  const onBoard = boardOrders(orders, channels, now, leadMinutes)
  const filtered =
    filterStatus === 'all'
      ? onBoard
      : filterStatus === 'active'
        ? activeOrders(onBoard)
        : onBoard.filter((order) => order.status === filterStatus)
  return sortForBoard(filtered, channels)
}

/**
 * The bands the board is drawn in, in road order — §9.104.
 *
 * <p>Waiting to be accepted, accepted, being cooked, cooked, checked, gone, arrived. `pending` leads
 * because an unaccepted order is the one thing on the board nobody has looked at yet; the status enum's
 * own order is alphabetical and means nothing to a dispatcher.
 *
 * <p>Matches legacy `dispatch.tsx:1693` band for band, which is what the owner asked for: *"we need
 * that rozvoz - nástenka to be kinda the same way"*.
 */
export const BOARD_BANDS = [
  { status: 'pending', label: tt('Čakajú na prijatie'), dot: 'bg-red-500' },
  { status: 'new', label: tt('Nové objednávky'), dot: 'bg-green-500' },
  { status: 'preparing', label: tt('V príprave'), dot: 'bg-violet-500' },
  { status: 'ready', label: tt('Pripravené'), dot: 'bg-amber-500' },
  { status: 'quality_check', label: tt('Kontrola kvality'), dot: 'bg-blue-500' },
  { status: 'on_the_way', label: tt('Na ceste'), dot: 'bg-zinc-500' },
  { status: 'at_address', label: tt('Ide na adresu'), dot: 'bg-orange-500' },
] as const

export interface BoardBand {
  status: string
  label: string
  dot: string
  orders: BoardOrder[]
}

/**
 * The board's orders split into their bands.
 *
 * <p><b>Empty bands are dropped</b>, as legacy drops them — seven headers over an empty evening is
 * six lines of furniture between the dispatcher and the one order that matters.
 *
 * <p><b>Anything in a status no band names is kept, in a band of its own at the end.</b> Silently
 * dropping it would take an order off the board because a status was added to the backend and not
 * here — the board's whole job is that nothing on it is invisible.
 */
export function boardBands(orders: BoardOrder[]): BoardBand[] {
  /* §9.643, owner O34 - **§9.318's own band is gone, because nothing can reach it any more.**
   *
   * That band held a preorder for a later day, so it would not be mixed into *Nové objednávky* and started
   * tonight. `boardOrders` now keeps a not-yet-due preorder off the board entirely, which is the owner's
   * instruction, so the band could only ever be empty - and an empty band is dropped by the rule above.
   * Keeping it would be dead code that reads like a feature. A preorder that **has** come due is ordinary
   * work and belongs in the band of the status it carries, which is where it now goes. */
  const today = orders

  const bands: BoardBand[] = BOARD_BANDS.map((band) => ({
    ...band,
    orders: today.filter((order) => order.status === band.status),
  })).filter((band) => band.orders.length > 0)

  const named = new Set<string>(BOARD_BANDS.map((band) => band.status))
  const rest = today.filter((order) => !named.has(String(order.status)))
  if (rest.length > 0) {
    bands.push({ status: 'other', label: tt('Ostatné'), dot: 'bg-muted-foreground', orders: rest })
  }

  return bands
}

export interface DriverLoad {
  driverId: number
  name: string
  orderCount: number
  /** In cents — §14.7. */
  totalCents: number
}

const OUT_ON_THE_ROAD = ['on_the_way', 'at_address']

/**
 * How long the van has been out - §9.438.
 *
 * **A different question from the one beside it, and that is the whole entry.** §9.154 put
 * `elapsedLabel(order.createdAt)` on the card as *"the answer to what has been waiting longest,
 * which this board existed to give and could not be asked"* - correct, and for an order already on
 * the road it conflates the kitchen with the road. *Accepted 47 minutes ago* is true whether the van
 * left two minutes ago or twenty-five, and **those are opposite decisions**: one is fine, the other
 * is a phone call to the driver.
 *
 * **`null` for anything but the two statuses it means something for.** An order still in the kitchen
 * has not departed, so *out for 0 min* would be a measurement of nothing - and `OUT_ON_THE_ROAD` is
 * used rather than a second copy of that list, because this file already owns it
 * (`agos-one-reader-spelled-twice`).
 *
 * **And `null` when the stamp is missing, measured rather than assumed.** The first version of this
 * comment guessed that the restored dump predates the column, and that is false: **7 316 of 7 436
 * delivery orders carry a departure stamp**, and the 120 that do not are **116 cancelled plus four
 * still inside the kitchen** - orders that never left, spanning the same date range as the rest. So
 * the real guard is against an order that never departed, plus the one code path that removes a
 * stamp from an order that did: `OrderStatusService` clears `on_the_way_at` on a reopen. Either way
 * the honest answer is to draw nothing rather than to date the journey from the epoch.
 *
 * `now` is a parameter for the reason this file gives at the top of its helpers: *"a dispatch board
 * is about the clock: a helper that calls `Date.now()` itself can only be tested at the moment the
 * test runs."*
 */
export function onRoadLabel(order: BoardOrder, now: number): string | null {
  if (!OUT_ON_THE_ROAD.includes(order.status ?? '')) return null
  const departed = order.onTheWayAt
  if (departed === undefined || departed === null || departed === '') return null
  return elapsedLabel(departed, now)
}

/**
 * Who is out, with how many bags and how much money.
 *
 * FB-058: legacy sums `o.total ?? 0` with `+`, and `total` arrives as a `numeric`
 * string, so the figure concatenates into nonsense as soon as a driver has two
 * orders. Assembled with `sumCents` here.
 *
 * Busiest driver first — the dispatcher's next decision is who *not* to give the
 * next order to.
 */
export function driverLoads(orders: BoardOrder[]): DriverLoad[] {
  const byDriver = new Map<number, { name: string; totals: Array<number | string | undefined> }>()

  for (const order of orders) {
    if (!OUT_ON_THE_ROAD.includes(order.status ?? '') || !order.driverId) continue
    const driverId = Number(order.driverId)
    const existing = byDriver.get(driverId)
    if (existing) {
      existing.totals.push(order.total)
    } else {
      byDriver.set(driverId, {
        name: order.driverName || `${tt('Šofér')} #${driverId}`,
        totals: [order.total],
      })
    }
  }

  return [...byDriver.entries()]
    .map(([driverId, driver]) => ({
      driverId,
      name: driver.name,
      orderCount: driver.totals.length,
      totalCents: sumCents(...driver.totals),
    }))
    .sort((left, right) => right.orderCount - left.orderCount)
}

export interface ArrivalNotice {
  /** Taken on the web and not yet accepted — the urgent ones. */
  pending: BoardOrder[]
  /** Accepted and now in the process. */
  accepted: BoardOrder[]
}

/**
 * Which orders are new since the last look.
 *
 * `seen` being `null` means this is the first load, and the first load announces
 * nothing — a dispatcher opening the board to nine waiting orders does not need
 * to be told nine orders arrived. Every later comparison does announce, including
 * from an empty board, which is the case the kitchen's version got wrong
 * (FB-047).
 */
export function arrivalsSince(
  orders: BoardOrder[],
  seen: ReadonlySet<number> | null,
): ArrivalNotice {
  if (seen === null) return { pending: [], accepted: [] }

  const fresh = orders.filter(
    (order) =>
      (order.status === 'new' || order.status === 'pending') && !seen.has(order.id ?? 0),
  )

  return {
    pending: fresh.filter((order) => order.status === 'pending'),
    accepted: fresh.filter((order) => order.status === 'new'),
  }
}

export function orderIdsOf(orders: BoardOrder[]): Set<number> {
  return new Set(orders.map((order) => order.id ?? 0))
}

/**
 * What the board announces and remembers after a read - FB-997, §9.677.
 *
 * <p>`orders` is `undefined` for a read that has **not answered**, which leaves the memory as it was and announces
 * nothing. An answered read is remembered **whatever it held, empty included**. The board's effect returned early on
 * `allOrders.length === 0`, which cannot tell *not loaded* from *loaded and empty*, so a board opened before the day's
 * first order kept no memory and that first order was taken for the first load: no toast and no sound, for the order
 * a dispatcher on an empty morning is waiting for. Legacy's board has the identical early return.
 */
export function boardArrivalStep(
  orders: BoardOrder[] | undefined,
  seen: Set<number> | null,
): { notice: ArrivalNotice; seen: Set<number> | null } {
  if (orders === undefined) return { notice: { pending: [], accepted: [] }, seen }
  return { notice: arrivalsSince(orders, seen), seen: orderIdsOf(orders) }
}

/**
 * What the settling dialog says it is about.
 *
 * Names the **status** as well as the order, because the same dialog now handles all three: a
 * heading reading *Stornovať objednávku* over a close would be the dialog lying about what the
 * button does.
 */
/**
 * Who the bag is for - §9.437, FB-760.
 *
 * **The owner:** *"they did not assigned an name for them, and in rozvoz - nástenka, instead of "-"
 * there is name of the assigned rozvozár."*
 *
 * **The board used to answer `order.customerName ?? order.driverName ?? '-'`**, and the comment over
 * that line was §9.125's: *"This board decides which car it goes in and **showed only the driver**,
 * so the name, the number and the address were on every other screen but this one."* §9.125 put the
 * customer's name on the board and left the driver as the fallback, which restores the very defect it
 * was written to fix - for every order whose customer has no name, which a phoned order taken by a
 * waitress usually is. It reads as the driver having ordered his own delivery, and a dispatcher
 * scanning a column of names has no reason to doubt it.
 *
 * **Blank counts as absent, which `??` does not do.** `Order.customerName` is a nullable column
 * published straight through `OrderDtos`, so the wire carries `null` for an untouched field and
 * `""` for one a till saved empty - and the owner's case is the second. A bare `?? '-'` would have
 * left that cell blank instead of dashed, which is a different way of saying nothing.
 *
 * **No driver in the fallback chain, ever.** A dash in a column of names says *nobody told us*; the
 * driver's name there is a false statement about a customer.
 */
export function customerLabel(order: Pick<BoardOrder, 'customerName'>): string {
  const name = (order.customerName ?? '').trim()
  return name === '' ? '-' : name
}

/**
 * The same question for a `title` attribute, where the dash has nothing to explain.
 *
 * A tooltip repeating `-` over a cell that already reads `-` is noise, so an order with no name
 * carries no tooltip at all - which is also what stops a hover from covering the row beneath it.
 */
export function customerTitle(order: Pick<BoardOrder, 'customerName'>): string {
  const label = customerLabel(order)
  return label === '-' ? '' : label
}

export function statusChangeSubtitle(order: BoardOrder, status: string): string {
  const who = order.customerName ?? `#${order.orderNumber ?? order.id ?? ''}`
  if (status === 'cancelled') return `${tt('Storno')}: ${who}`
  if (status === 'quality_check') return `${tt('Vrátiť do kuchyne')}: ${who}`
  return `${tt('Uzavrieť')}: ${who}`
}

/**
 * The dialog's heading - §9.409.
 *
 * **The comment above was right and the subtitle was not enough.** The heading and the confirm
 * button are the two things somebody reads before pressing, and both were hard-coded to the cancel
 * wording, so pressing *Vydaná* opened a dialog headed *Stornovať objednávku* over a subtitle
 * reading *Uzavrieť*. The owner: *"we will see 'Stornovať objednávku' when clicking vydané."*
 *
 * **The wording follows the subtitle rather than the button on the card.** `Vydaná` and `Doručená`
 * are two buttons for one status - in-house and delivery - so a heading naming either would be
 * wrong half the time. `Uzavrieť` already covers both, which is why `statusChangeSubtitle` chose it.
 */
export function statusChangeTitle(status: string): string {
  if (status === 'cancelled') return tt('Stornovať objednávku')
  if (status === 'quality_check') return tt('Vrátiť do kuchyne')
  return tt('Uzavrieť objednávku')
}

/**
 * The label on the button that commits it - §9.409.
 *
 * Beside the heading rather than derived from it, and decided by the same `status`: a button reading
 * *Stornovať* under a heading reading *Uzavrieť objednávku* is the same defect one layer down.
 */
export function statusChangeConfirmLabel(status: string): string {
  if (status === 'cancelled') return tt('Stornovať')
  if (status === 'quality_check') return tt('Vrátiť')
  return tt('Uzavrieť')
}

/**
 * Whether the money is confirmed, for an order paid by card online - §9.417, FB-744 reports 4 and 6.
 *
 * *"if Platba were schválená"*, and then *"when we are waiting for cardpay overovanie, make it
 * yellow and some Čaká sa... check old system for that one."*
 *
 * **Legacy has it and AGOS did not** (`dispatch.tsx:471`), so this is a port - with **one deliberate
 * departure the owner asked for and is right about.** Legacy paints the waiting state **red**, which
 * says *failed*; they asked for yellow, which says *waiting*. Legacy's own `online-payments.tsx`
 * uses `amber` for `pending`, so legacy contradicts itself and the owner is asking for the half that
 * is consistent.
 *
 * **Only for `card_online`**, exactly as legacy scopes it: cash has nothing to verify, and a badge
 * saying *waiting* on a cash order would be a question nobody can answer.
 *
 * Three states rather than approved/not-approved, because they are three different facts for
 * somebody deciding whether to send a bag: paid, waiting, and refused.
 */
export function paymentApprovalBadge(
  paymentMethod: string | undefined,
  paymentStatus: string | undefined,
): { label: string; tone: string } | null {
  if ((paymentMethod ?? '').trim().toLowerCase() !== 'card_online') return null
  const status = (paymentStatus ?? '').trim().toLowerCase()
  if (status === 'online_paid') {
    return {
      label: tt('✓ Platba schválená'),
      tone: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    }
  }
  if (status === 'online_failed' || status === 'failed') {
    return {
      label: tt('✕ Platba neúspešná'),
      tone: 'bg-red-500/15 text-red-400 border-red-500/30',
    }
  }
  // Everything else is *not confirmed yet*, including an absent status: the verification is the
  // thing being waited on, so unknown and pending are the same fact to a dispatcher.
  return {
    label: tt('⏳ Čaká sa na overenie'),
    tone: 'bg-amber-500/15 text-amber-500 border-amber-500/35',
  }
}

/**
 * Whether the row draws *Vyraziť*, and whether it is usable - §9.417, FB-744 report 5.
 *
 * The owner: *"when šofér is assigned, and objednávka is not in state where you can use Button
 * Vyraziť keep that button there everywhere, and disable it - but only when Šofér is assigned, if he
 * is not assigned, do not show it."*
 *
 * **It is a better rule than the one it replaces, and worth saying why.** The button used to appear
 * only when the transition was available, so it came and went - and a control that moves teaches
 * nobody where it lives. Present-but-disabled says *this is the button and it is not your turn*;
 * absent says *there is nobody to send*, which is a different statement and the honest one when no
 * driver is assigned.
 *
 * **The enabling rule is `isDispatchable`, and the first version of this got it wrong.** It read
 * `transitions.includes('on_the_way')` - and `NEXT_STATUSES` has **no `on_the_way` target
 * anywhere**, so the button was disabled in every state and the owner said so: *"vyraziť tlačítko
 * is still not clickable when its vyhodené z kuchyne (like Kontrola Kvality)."* An order does not
 * reach `on_the_way` by a status change at all; it leaves through `bulk-dispatch`, and
 * `isDispatchable` is the rule that already says when it may - `quality_check` **and** a driver,
 * with its own comment explaining that dispatching earlier *"sends out a bag nobody checked"*.
 *
 * So this now shares the bar's rule rather than inventing a second one, which is what made the
 * first attempt wrong rather than merely different.
 *
 * **§9.728, owner O98 - and whether this person may send it at all.** *Vyraziť* is the right
 * `dispatch_order` since BACKEND.md §10A.376; a driver who held only the board was shown the button and
 * refused by the server with nothing to say why. Without the right the button is still drawn - the rule
 * above, *this is the button and it is not your turn* - and disabled with the right's name, which is what
 * the person can ask for. `mayDispatch` is **required**: §9.480's rule, an optional flag is dropped at a
 * call site with nothing to say so. The reason comes with the state so the card cannot show one without
 * the other.
 */
export function dispatchButtonFor(
  order: { driverId?: number | null; status?: string },
  mayDispatch: boolean,
): { shown: boolean; enabled: boolean; reason: string | null } {
  const hasDriver = typeof order.driverId === 'number' && order.driverId > 0
  if (!hasDriver) return { shown: false, enabled: false, reason: null }
  if (!mayDispatch) return { shown: true, enabled: false, reason: dispatchRefusedNote() }
  if (!isDispatchable(order)) {
    return { shown: true, enabled: false, reason: tt('Objednávka musí byť v stave Kontrola kvality') }
  }
  return { shown: true, enabled: true, reason: null }
}

/** The right *Vyraziť* asks for - BACKEND.md §10A.376. */
export const DISPATCH_ORDER = 'dispatch_order'

/**
 * Why *Vyraziť* is disabled for somebody without the right - §9.728.
 *
 * Names the **right**, as `statusRoleNote` does for a cancel (§9.653): *a dispatcher* or *an administrator*
 * would send a driver who has been granted it looking for a person, when what they need is a tick in
 * *Oprávnenia*. The right's own label, so the sentence and the roles screen use one name.
 */
export function dispatchRefusedNote(): string {
  return `${tt('Na vyrazenie je potrebné právo')} „${tt('Vyraziť objednávky na adresu')}".`
}
